import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Alert, Spinner, Table } from "reactstrap";
import { AppContext } from "../../context";
import { getOpsProjects } from "../production/service";
import {
  formatStoreOrderLabel,
  toErrorText,
} from "./storeSalesDashboardHelpers";
import "./StoreSalesDashboard.css";

const PART_ITEM_STATUS_SEGMENTS = [
  { key: "draft", label: "Draft", color: "#dc3545" },
  { key: "planning", label: "Planning", color: "#dc3545" },
  { key: "production", label: "Production", color: "#ffc107" },
  { key: "in_stock", label: "In Stock", color: "#0d6efd" },
  { key: "packed", label: "Packed", color: "#198754" },
];

const PART_ITEM_STATUS_LEGEND = [
  { label: "Draft / Planning", color: "#dc3545" },
  { label: "Production", color: "#ffc107" },
  { label: "In Stock", color: "#0d6efd" },
  { label: "Packed", color: "#198754" },
];

const OPS_PROJECT_TABS = [
  { value: "in_progress", label: "In Progress" },
  { value: "delivered", label: "Delivered" },
];

const OPS_DASHBOARD_LINKS = [
  { to: "/ops/production", label: "Ops Production" },
  { to: "/stock/dashboard", label: "Stock Dashboard" },
  { to: "/stock/production", label: "Stock Production" },
  { to: "/scan/panel", label: "Panel In-stock Scan" },
  { to: "/scan/package", label: "Package Load Scan" },
];

const OPS_IN_PROGRESS_STATUSES = new Set([
  "accepted",
  "dispatched",
  "partially_delivered",
]);

const OPS_DELIVERED_STATUSES = new Set(["delivered"]);

const OPS_PROJECTS_TABLE_COL_SPAN = 5;

function normalizeProjectStatus(status) {
  return String(status ?? "")
    .trim()
    .toLowerCase();
}

function partItemsStatusBar(byStatus, total) {
  const totalCount = Number(total);
  if (!Number.isFinite(totalCount) || totalCount <= 0) {
    return <span className="text-muted">—</span>;
  }

  const segments = PART_ITEM_STATUS_SEGMENTS.map((segment) => {
    const count = Number(byStatus[segment.key]);
    if (!Number.isFinite(count) || count <= 0) return null;

    return {
      ...segment,
      count,
      widthPct: (count / totalCount) * 100,
    };
  }).filter(Boolean);

  if (segments.length === 0) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="store-ops-parts-bar w-100">
      <div className="store-ops-parts-bar__track d-flex w-100 rounded overflow-hidden border bg-light">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="store-ops-parts-bar__segment"
            style={{
              width: `${segment.widthPct}%`,
              backgroundColor: segment.color,
            }}
            title={`${segment.label}: ${segment.count}`}
          />
        ))}
      </div>
      <div className="store-ops-parts-bar__labels d-flex w-100">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="store-ops-parts-bar__label small fw-bold text-dark"
            style={{ width: `${segment.widthPct}%` }}
            title={`${segment.label}: ${segment.count}`}
          >
            {segment.count}
          </div>
        ))}
      </div>
    </div>
  );
}

const StoreOpsDashboard = () => {
  const { currentUser } = useContext(AppContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("in_progress");

  const { inProgressProjects, deliveredProjects } = useMemo(() => {
    const inProgress = [];
    const delivered = [];

    for (const project of projects) {
      const status = normalizeProjectStatus(project.status);
      if (OPS_IN_PROGRESS_STATUSES.has(status)) {
        inProgress.push(project);
      } else if (OPS_DELIVERED_STATUSES.has(status)) {
        delivered.push(project);
      }
    }

    return { inProgressProjects: inProgress, deliveredProjects: delivered };
  }, [projects]);

  const activeProjects =
    activeTab === "delivered" ? deliveredProjects : inProgressProjects;

  const emptyMessage =
    activeTab === "delivered"
      ? "No delivered projects."
      : "No in-progress projects.";

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        activeTab === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [activeTab]
  );

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getOpsProjects();
      setProjects(rows);
    } catch (err) {
      setProjects([]);
      setError(toErrorText(err, "Unable to load projects."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser?.admin) return;
    loadProjects();
  }, [currentUser?.admin, loadProjects]);

  if (!currentUser?.admin) {
    if (currentUser) {
      return (
        <div className="min-vh-100 w-100 mw-100 bg-light">
          <div className="container-fluid py-4 px-3 px-lg-4 w-100 mw-100">
            <Alert color="danger" fade={false}>
              UNAUTHORIZED
            </Alert>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div className="min-vh-100 w-100 mw-100 bg-light">
      <div className="container-fluid py-4 px-3 px-lg-4 w-100 mw-100">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <h1 className="h4 fw-bold mb-0">Ops dashboard</h1>
          <div className="d-flex flex-wrap gap-3">
            {OPS_DASHBOARD_LINKS.map((link, index) => (
              <Link
                key={index}
                to={link.to}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="store-dashboard-tab-panel rounded border">
          {error ? (
            <Alert color="danger" className="mb-0" fade={false}>
              {error}
            </Alert>
          ) : (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {OPS_PROJECT_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    className={pillClassName(tab.value)}
                    onClick={() => setActiveTab(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {loading ? (
                <div className="py-4 text-center text-muted">
                  <Spinner className="me-2" />
                  Loading…
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <div className="small text-muted">
                      {activeProjects.length}{" "}
                      {activeProjects.length === 1 ? "row" : "rows"}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-3 small text-muted">
                      {PART_ITEM_STATUS_LEGEND.map((item) => (
                        <span
                          key={item.label}
                          className="d-inline-flex align-items-center gap-1"
                        >
                          <span
                            className="rounded flex-shrink-0"
                            style={{
                              width: "0.75rem",
                              height: "0.75rem",
                              backgroundColor: item.color,
                            }}
                            aria-hidden
                          />
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Table
                    bordered
                    hover
                    responsive
                    className="mb-0 store-sales-table store-ops-projects-table"
                  >
                    <colgroup>
                      <col />
                      <col />
                      <col className="store-ops-projects-table__total-col" />
                      <col className="store-ops-projects-table__parts-col" />
                      <col />
                    </colgroup>
                    <thead className="store-sales-table__head">
                      <tr>
                        <th>Buyer</th>
                        <th>Project</th>
                        <th>Total</th>
                        <th>Parts</th>
                        <th>Batches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeProjects.length === 0 ? (
                        <tr>
                          <td
                            colSpan={OPS_PROJECTS_TABLE_COL_SPAN}
                            className="text-muted text-center py-4"
                          >
                            {emptyMessage}
                          </td>
                        </tr>
                      ) : (
                        activeProjects.map((project) => {
                          const byStatus = project.part_items?.by_status ?? {};
                          const total = project.part_items?.total ?? 0;

                          return (
                            <tr key={project.id}>
                              <td className="small">{project.buyer}</td>
                              <td>
                                <Link
                                  to={`/projects/${project.id}`}
                                  className="align-baseline text-decoration-none small"
                                >
                                  <div className="d-flex flex-wrap align-items-baseline gap-2">
                                    {formatStoreOrderLabel(project)}
                                  </div>
                                </Link>
                              </td>
                              <td className="small">{total}</td>
                              <td className="small">
                                {partItemsStatusBar(byStatus, total)}
                              </td>
                              <td className="small">
                                {project.batches?.length ? (
                                  <div className="d-flex flex-column gap-1">
                                    {project.batches.map((batch, index) => (
                                      <div key={index}>
                                        <div>
                                          {batch.name}{" "}
                                          <span className="text-muted text-capitalize">
                                            ({batch.status})
                                          </span>
                                          <span className="text-muted text-capitalize">
                                            - {batch.factory}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreOpsDashboard;
